import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import type { CaApiClient } from '../../shared/types'

export interface DeleteConversationDialogProps {
  open: boolean
  conversationId?: string
  conversationTitle?: string
  client: CaApiClient
  onClose: () => void
  onDeleted: (id: string) => void
}

export default function DeleteConversationDialog(props: DeleteConversationDialogProps) {
  const intl = useIntl()
  const { open, conversationId, conversationTitle, client, onClose, onDeleted } = props
  const qc = useQueryClient()
  const [error, setError] = useState<string | undefined>()

  const shares = useQuery(
    ['ca', 'shares', conversationId],
    () => client.listShares(conversationId as string),
    { enabled: open && !!conversationId },
  )

  const del = useMutation(() => client.deleteConversation(conversationId as string), {
    onSuccess: () => {
      qc.invalidateQueries(['ca', 'conversations'])
      onDeleted(conversationId as string)
      onClose()
    },
    onError: (e: unknown) => setError(cleanError(e)),
  })

  const count = shares.data?.length ?? 0

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth={true}>
      <DialogTitle>{intl.formatMessage({ id: 'caChat.deleteDialog.title' })}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          {conversationTitle
            ? intl.formatMessage({ id: 'caChat.deleteDialog.body' }, { title: conversationTitle })
            : intl.formatMessage({ id: 'caChat.deleteDialog.body.fallback' })}
        </Typography>
        {count > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {count === 1
              ? intl.formatMessage({ id: 'caChat.deleteDialog.warning.shared.one' })
              : intl.formatMessage({ id: 'caChat.deleteDialog.warning.shared.many' }, { count })}
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={del.isLoading}>{intl.formatMessage({ id: 'caChat.deleteDialog.button.cancel' })}</Button>
        <Button
          color="error"
          variant="contained"
          onClick={() => del.mutate()}
          disabled={del.isLoading}
          startIcon={del.isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {del.isLoading ? intl.formatMessage({ id: 'caChat.deleteDialog.button.deleting' }) : intl.formatMessage({ id: 'caChat.deleteDialog.button.delete' })}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function cleanError(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e)
  return msg.replace(/^CA API \d+:\s*/, '')
}
